/**
 * Parties (ปาร์ตี้): two to five fighters travelling together. Until real players arrive online the
 * party is made of fellow wanderers met in the world — simulated players who follow the leader,
 * fight what the party fights and come back to their feet after falling. Training points from a
 * kill are shared by every member near it, with a bonus for each extra member, so hunting together
 * is quicker than hunting alone.
 */
import type { ZoneId } from './zones';

export const PARTY = {
  /** Members, counting the player. */
  maxSize: 5,
  minSize: 2,
  /** Members within this many tiles of a kill share its training points (and gold). */
  shareRange: 14,
  /** The kill's training points grow by this much per member sharing it beyond the first. */
  bonusPerMember: 0.1,
  /** How close a fellow has to be to be invited. */
  inviteRange: 6,
  /** A fallen member gets up again beside the leader after this many seconds. */
  reviveSeconds: 10,
  /** Members keep about this far from the leader when there is nothing to fight. */
  followDistance: 2.2,
  /** Further than this and a member runs (and further still, catches up at once). */
  catchUp: 9,
  teleport: 22,
  /** Members join a fight against foes within this many tiles of the leader. */
  assistRange: 10,
  /** Fellows' strength relative to the player's. */
  strength: 0.85,
} as const;

/** A fellow wanderer: a simulated player who can be asked into the party. */
export interface FellowDef {
  readonly id: string;
  readonly name: string;
  readonly title: string;
  /** Sprite key (a school uniform). */
  readonly sprite: string;
  /** The martial art they fight with. */
  readonly art: string;
  /** Where they wait (in the hub), relative to the plaza, in tiles. */
  readonly home: { readonly dx: number; readonly dy: number };
  readonly zone: ZoneId;
  /** What they say when asked to join. */
  readonly greeting: string;
  /** A neigong they have trained to its peak: they carry its aura (data/neigong). */
  readonly mastered?: string;
}

const list: FellowDef[] = [
  {
    id: 'shadowless',
    name: 'ดาบไร้เงา',
    title: 'จอมยุทธ์พเนจร',
    sprite: 'heroWudang',
    art: 'snow-sword',
    home: { dx: 0.5, dy: 12 },
    zone: 'plains',
    greeting: 'เดินทางคนเดียวมานาน มีเพื่อนร่วมทางก็ดี',
    mastered: 'mist-breath',
  },
  {
    id: 'ironmonk',
    name: 'หลวงจีนเหล็กกล้า',
    title: 'ศิษย์สำนักหินระฆัง',
    sprite: 'heroShaolin',
    art: 'bell-fist',
    home: { dx: 6, dy: 5 },
    zone: 'plains',
    greeting: 'อามิตาพุทธ อาตมาจะคุ้มกันโยมเอง',
  },
  {
    id: 'crimsonguard',
    name: 'ชาดเหล็ก',
    title: 'ทางโคมเหล็ก',
    sprite: 'heroJinyiweiF',
    art: 'lamp-hook-sabre',
    home: { dx: -6, dy: 5 },
    zone: 'plains',
    greeting: 'ได้ ข้าจะไปด้วย อย่าให้ข้าต้องผิดหวัง',
  },
  {
    id: 'peachblossom',
    name: 'ท้อสะคราญ',
    title: 'ธิดาหุบเขาจันทร์มัว',
    sprite: 'heroJileguF',
    art: 'quiet-palm',
    home: { dx: 7, dy: -4 },
    zone: 'plains',
    greeting: 'ไปด้วยกันสิ การต่อสู้คนเดียวมันน่าเบื่อจะตาย',
    mastered: 'mourning-heart',
  },
  {
    id: 'inkbrush',
    name: 'พู่กันหมึกเขียว',
    title: 'บัณฑิตหมู่ตึก',
    sprite: 'heroJunzitangF',
    art: 'ink-slate',
    home: { dx: -7, dy: -4 },
    zone: 'plains',
    greeting: 'การเดินทางย่อมดีกว่าเมื่อมีสหายร่วมทาง',
  },
  {
    id: 'redblade',
    name: 'ใบมีดโลหิต',
    title: 'ศิษย์สำนักดาบชาด',
    sprite: 'heroBlood',
    art: 'heat-sabre',
    home: { dx: 3, dy: -8 },
    zone: 'plains',
    greeting: 'ฮึ ขอแค่มีให้ฟันก็พอ',
    mastered: 'ember-heart',
  },
];

export const FELLOWS: Readonly<Record<string, FellowDef>> = Object.fromEntries(list.map((f) => [f.id, f]));
export const FELLOW_LIST: readonly FellowDef[] = list;

/**
 * Training points each member near a kill receives: the kill's worth with the party bonus, split
 * evenly between them (alone: the whole worth).
 */
export function partyShare(worth: number, sharing: number): number {
  const n = Math.max(1, Math.min(PARTY.maxSize, sharing));
  return Math.round((worth * (1 + PARTY.bonusPerMember * (n - 1))) / n);
}
